/**
 * i18n Client Configuration
 * Client-side i18n setup using react-i18next.
 *
 * Uses full locale codes (e.g., 'en-US') for language keys so that
 * translation files are loaded from /locales/en-US/, /locales/en-CA/, etc.
 *
 * Initialization happens eagerly at module level so translations are loaded
 * before components render. The exported `i18nReady` promise resolves once
 * all initial namespaces are loaded.
 */

'use client';

import i18next from 'i18next';
import { initReactI18next, useTranslation as useTranslationBase } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import { i18nConfig, Namespace, defaultNamespace, fallbackLngMap } from './config';
import type { Locale } from './config';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '../intl/intl.config';
import type { SupportedLocale } from '../intl/intl.config';

/**
 * Detect the initial locale from the current URL path so translations for the
 * correct language are fetched on the very first load (avoiding an en-US flash).
 */
function detectLocaleFromURL(): SupportedLocale {
  if (globalThis.window === undefined) return DEFAULT_LOCALE;

  const first = globalThis.location.pathname.split('/').find(Boolean);

  if (first && (SUPPORTED_LOCALES as readonly string[]).includes(first)) {
    return first as SupportedLocale;
  }

  return DEFAULT_LOCALE;
}

// Eagerly initialize i18next at module load time.
const initialLocale = detectLocaleFromURL();

const i18nReady: Promise<any> = i18next
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: initialLocale,
    fallbackLng: fallbackLngMap,
    supportedLngs: [...i18nConfig.locales],
    defaultNS: defaultNamespace,
    ns: [...i18nConfig.namespaces],
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

export { i18nReady };

/**
 * Change language (and load new namespace files if needed).
 * Returns a promise that resolves once the new language's translations are ready.
 */
export function changeLanguage(locale: Locale): Promise<any> {
  if (i18next.language === locale) return Promise.resolve();
  return i18next.changeLanguage(locale);
}

/**
 * Hook for translations.
 * @param ns - Namespace to use
 */
export function useTranslation(ns: Namespace = defaultNamespace) {
  return useTranslationBase(ns);
}

/**
 * Get current language (full locale code, e.g., 'en-US')
 */
export function getCurrentLanguage(): string {
  return i18next.language || i18nConfig.defaultLocale;
}

/**
 * Reset the initialization state (for testing purposes only)
 */
export function resetI18n(): void {
  // No-op in the new eager-init model; kept for test-file compatibility.
}
